import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './LinkInvite.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface InviteInfo {
  status: 'valid' | 'used' | 'expired' | 'invalid';
  requireLogin?: boolean;
  hostName?: string;
}

// Result codes redirected back from the OAuth callback (?status=...).
const RESULT_MESSAGES: Record<string, string> = {
  used: 'This invite link was already used. Ask the sender for a new one.',
  expired: 'This invite link has expired. Ask the sender for a new one.',
  invalid: 'This invite link is not valid. Ask the sender for a new one.',
  tenant_linking_disabled: 'Tenant linking is not available right now.',
  platform_not_allowed: 'YouTube linking is not available right now.',
  tenant_limit_reached: 'The sender has reached their tenant channel limit.',
  channel_already_hosted: 'This channel is already linked to another user.',
  cannot_host_own_channel: 'This is the sender’s own channel — nothing to link.',
  no_youtube_channel: 'This Google account has no YouTube channel. Pick the account or channel that has one and try again.',
  link_failed: 'Linking failed. Please try again with the same link, or ask for a new one.',
};

const LinkInvite: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading } = useAuth();
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const resultStatus = searchParams.get('status');

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/link-invites/${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((data: InviteInfo) => { if (!cancelled) setInfo(data); })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, [token]);

  const handleContinue = () => {
    window.location.href = `${API_BASE}/auth/youtube/invite/${encodeURIComponent(token)}`;
  };

  let body: React.ReactNode;
  if (resultStatus === 'linked') {
    body = (
      <>
        <h1 className="li-title">Channel linked</h1>
        <p className="li-text">Your YouTube channel is now linked. You can close this page.</p>
        <p className="li-note">
          Want to manage your own channel too? <Link to="/login">Join VimAPI</Link>.
        </p>
      </>
    );
  } else if (resultStatus && RESULT_MESSAGES[resultStatus]) {
    body = (
      <>
        <h1 className="li-title">Couldn’t link the channel</h1>
        <p className="li-text">{RESULT_MESSAGES[resultStatus]}</p>
      </>
    );
  } else if (loadFailed) {
    body = <p className="li-text">Could not load this invite. Please try again.</p>;
  } else if (!info || isLoading) {
    body = <p className="li-text">Loading…</p>;
  } else if (info.status !== 'valid') {
    body = (
      <>
        <h1 className="li-title">Invite unavailable</h1>
        <p className="li-text">{RESULT_MESSAGES[info.status]}</p>
      </>
    );
  } else {
    const needsLogin = info.requireLogin && !isAuthenticated;
    body = (
      <>
        <h1 className="li-title">Link your YouTube channel</h1>
        <p className="li-text">
          <strong>{info.hostName}</strong> invited you to link your YouTube channel so they can publish
          to it and see its analytics. You can unlink at any time from your Google account settings.
        </p>
        {info.requireLogin && (
          <p className="li-note">The sender requires you to log in to VimAPI first.</p>
        )}
        {needsLogin ? (
          <>
            <Link className="li-btn" to="/login">Log in to VimAPI</Link>
            <p className="li-note">After logging in, open this invite link again to continue.</p>
          </>
        ) : (
          <button type="button" className="li-btn" onClick={handleContinue}>
            Continue with Google
          </button>
        )}
      </>
    );
  }

  return (
    <div className="li-page">
      <div className="li-card">{body}</div>
    </div>
  );
};

export default LinkInvite;
